import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3, Smile, Frown, Meh, TrendingUp, MessageSquare, Download, RefreshCw,
  Users, GraduationCap, Award, ExternalLink, Sparkles, FilterX, FileText, FileBarChart2, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { toast } from "sonner";

const NPS_COLORS = {
  excelente: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", icon: "text-emerald-600", label: "Excelente" },
  muito_bom: { bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-800", icon: "text-sky-600", label: "Muito Bom" },
  razoavel: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", icon: "text-amber-600", label: "Razoável" },
  critico: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", icon: "text-rose-600", label: "Crítico" },
};

function NpsBadge({ classification }) {
  const c = NPS_COLORS[classification] || NPS_COLORS.razoavel;
  const Icon = classification === "excelente" || classification === "muito_bom" ? Smile : classification === "critico" ? Frown : Meh;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${c.bg} ${c.text} border ${c.border}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

function NpsCard({ label, value, classification, icon: Icon, sub }) {
  const c = NPS_COLORS[classification] || NPS_COLORS.razoavel;
  return (
    <Card className={`rounded-xl border-2 ${c.border} ${c.bg}/30`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
        <p className={`font-display text-3xl font-bold ${c.text}`}>
          {value >= 0 ? "+" : ""}{value}
        </p>
        <div className="mt-1.5 flex items-center justify-between">
          <NpsBadge classification={classification} />
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

const RATING_ORDER = ["otimo", "bom", "regular", "ruim", "pessimo"];
const RATING_COLORS = { otimo: "#059669", bom: "#0ea5e9", regular: "#f59e0b", ruim: "#f97316", pessimo: "#dc2626" };
const RATING_LABELS = { otimo: "Ótimo", bom: "Bom", regular: "Regular", ruim: "Ruim", pessimo: "Péssimo" };

/**
 * Renderiza o resumo GENÉRICO de qualquer template de pesquisa (padrão ou
 * personalizado), pergunta a pergunta, conforme o tipo.
 */
function DynamicSummary({ summary }) {
  if (!summary || !Array.isArray(summary.sections)) {
    return <p className="text-sm text-muted-foreground text-center py-8">Sem dados para exibir.</p>;
  }
  const totalText = (n) => `${n} resposta${n === 1 ? "" : "s"}`;

  return (
    <div className="space-y-5" data-testid="survey-dynamic-summary">
      <div className="text-xs text-muted-foreground">
        Modelo: <span className="font-medium text-[hsl(var(--primary))]">{summary.template_name || "—"}</span> · {totalText(summary.total_responses)}
      </div>
      {summary.sections.map((sec) => (
        <div key={sec.id} className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-[hsl(var(--primary))]">{sec.title}</p>
            {sec.description && <p className="text-[11px] text-muted-foreground">{sec.description}</p>}
          </div>
          {sec.questions.map((q) => (
            <Card key={q.id} className="rounded-lg">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-xs font-medium text-[hsl(var(--primary))] flex-1">{q.label}</p>
                  <Badge variant="outline" className="text-[10px] shrink-0">{q.total_answered} resp.</Badge>
                </div>

                {/* NPS 0-10 */}
                {q.type === "nps10" && (
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">NPS</p>
                        <p className={`font-display text-2xl font-bold ${q.nps?.nps >= 50 ? "text-emerald-600" : q.nps?.nps >= 0 ? "text-amber-600" : "text-rose-600"}`}>
                          {q.nps?.nps >= 0 ? "+" : ""}{q.nps?.nps ?? 0}
                        </p>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Média: <span className="font-medium text-[hsl(var(--primary))]">{q.average ?? 0}</span><br />
                        Promotores {q.nps?.promoters || 0} · Neutros {q.nps?.passives || 0} · Detratores {q.nps?.detractors || 0}
                      </div>
                    </div>
                    <div className="flex h-16 items-end gap-0.5">
                      {Object.entries(q.histogram || {}).map(([score, count]) => {
                        const max = Math.max(1, ...Object.values(q.histogram || { 0: 1 }));
                        const h = (count / max) * 100;
                        const color = parseInt(score) >= 9 ? "#10b981" : parseInt(score) >= 7 ? "#f59e0b" : "#ef4444";
                        return (
                          <div key={score} className="flex-1 flex flex-col items-center justify-end" title={`Nota ${score}: ${count}`}>
                            <div className="w-full rounded-t" style={{ height: `${h}%`, backgroundColor: color, minHeight: count > 0 ? 3 : 0 }} />
                            <span className="text-[8px] text-muted-foreground mt-0.5">{score}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Rating (Ótimo..Péssimo) */}
                {q.type === "rating5" && (() => {
                  const dist = q.distribution || {};
                  const total = dist.total || 1;
                  return (
                    <div>
                      <div className="flex h-6 rounded-md overflow-hidden border border-[hsl(var(--border))]">
                        {RATING_ORDER.map((r) => {
                          const wv = ((dist[r] || 0) / total) * 100;
                          return wv > 0 ? <div key={r} style={{ width: `${wv}%`, backgroundColor: RATING_COLORS[r] }} title={`${RATING_LABELS[r]}: ${dist[r]}`} /> : null;
                        })}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
                        {RATING_ORDER.map((r) => (dist[r] > 0 ? (
                          <span key={r} className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: RATING_COLORS[r] }} />{RATING_LABELS[r]}: {dist[r]}
                          </span>
                        ) : null))}
                      </div>
                    </div>
                  );
                })()}

                {/* Sim / Não */}
                {q.type === "yesno" && (() => {
                  const total = (q.yes || 0) + (q.no || 0) || 1;
                  const yesPct = Math.round((q.yes || 0) / total * 100);
                  return (
                    <div>
                      <div className="flex h-6 rounded-md overflow-hidden border border-[hsl(var(--border))]">
                        {q.yes > 0 && <div style={{ width: `${yesPct}%`, backgroundColor: "#059669" }} title={`Sim: ${q.yes}`} />}
                        {q.no > 0 && <div style={{ width: `${100 - yesPct}%`, backgroundColor: "#94a3b8" }} title={`Não: ${q.no}`} />}
                      </div>
                      <div className="flex gap-3 mt-1.5 text-[10px]">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-600" />Sim: {q.yes || 0} ({yesPct}%)</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-400" />Não: {q.no || 0}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Múltipla escolha */}
                {q.type === "multiselect" && (
                  <div className="space-y-1.5">
                    {(q.options || []).length === 0 && <p className="text-[11px] text-muted-foreground">Sem respostas.</p>}
                    {(q.options || []).map((opt) => {
                      const total = q.total_respondents || 1;
                      const pct = Math.round((opt.count / total) * 100);
                      return (
                        <div key={opt.value}>
                          <div className="flex justify-between text-[11px] mb-0.5">
                            <span className="text-muted-foreground">{opt.label}</span>
                            <span className="font-medium">{opt.count} ({pct}%)</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                            <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Texto livre */}
                {q.type === "text" && (
                  (q.answers_text || []).length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">Nenhuma resposta de texto.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {(q.answers_text || []).map((t, i) => (
                        <div key={`${q.id}-txt-${i}`} className="text-xs italic text-[hsl(var(--primary))] border-l-2 border-[hsl(var(--accent))]/50 pl-2 py-0.5">"{t}"</div>
                      ))}
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ))}
    </div>
  );
}

function CourseDetail({ courseId, onClose, filtersDesc }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("respostas");
  const [pdfBusy, setPdfBusy] = useState(null); // null | "general" | "professor"

  useEffect(() => {
    if (!courseId) return;
    api.get(`/admin/surveys/course/${courseId}`).then((r) => setData(r.data)).catch(() => {});
  }, [courseId]);

  const onExport = async () => {
    try {
      const token = localStorage.getItem("ceap_token");
      const res = await fetch(`${API}/admin/surveys/course/${courseId}/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pesquisas_${(data?.course?.title || "curso").slice(0, 30).replace(/[^a-zA-Z0-9]+/g, "_")}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch {
      toast.error("Falha ao exportar");
    }
  };

  const downloadReport = async (scope) => {
    setPdfBusy(scope);
    try {
      const token = localStorage.getItem("ceap_token");
      const qs = new URLSearchParams({ scope });
      if (filtersDesc) qs.set("filters", filtersDesc);
      const res = await fetch(`${API}/admin/surveys/course/${courseId}/report.pdf?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const base = (data?.course?.title || "curso").slice(0, 30).replace(/[^a-zA-Z0-9]+/g, "_");
      a.href = url;
      a.download = `${scope === "professor" ? "relatorio_professor" : "relatorio_pesquisa"}_${base}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(scope === "professor" ? "Relatório do professor gerado" : "Relatório geral gerado");
    } catch {
      toast.error("Falha ao gerar o relatório PDF");
    } finally {
      setPdfBusy(null);
    }
  };

  if (!data) return <div className="p-6"><Skeleton className="h-40" /></div>;

  const histogramData = Object.entries(data.score_histogram || {}).map(([k, v]) => ({
    score: k, count: v,
    fill: parseInt(k) >= 9 ? "#10b981" : parseInt(k) >= 7 ? "#f59e0b" : "#ef4444",
  }));

  const distItems = [
    { key: "teacher_knowledge", label: "Conhecimento Prof." },
    { key: "teacher_clarity", label: "Tirar dúvidas" },
    { key: "teacher_material", label: "Material" },
    { key: "org_team", label: "Atendimento" },
    { key: "org_coffee", label: "Coffee Break" },
    { key: "org_infrastructure", label: "Infraestrutura" },
  ];

  const ratingColors = { otimo: "#059669", bom: "#0ea5e9", regular: "#f59e0b", ruim: "#f97316", pessimo: "#dc2626" };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Professor</p>
        <p className="text-sm font-medium text-[hsl(var(--primary))]">{data.course.professor || "—"} · {data.course.workload_hours}h · {data.course.cidade}/{data.course.estado}</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <NpsCard label="NPS Geral" value={data.nps.geral} classification={data.nps.classification} icon={Sparkles} sub={`${data.responses_count} resp.`} />
        <NpsCard label="Professores" value={data.nps.teachers} classification={data.nps.classification} icon={GraduationCap} />
        <NpsCard label="Organização" value={data.nps.organization} classification={data.nps.classification} icon={Users} />
      </div>

      {data.nps?.has_nps_questions === false && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-[11px] px-3 py-2">
          Esta pesquisa é personalizada e não possui perguntas de NPS (nota 0–10). Por isso os medidores acima ficam zerados —
          veja todas as respostas na aba <strong>Respostas</strong> e no <strong>CSV</strong>.
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Badge variant="outline">👍 Promotores: {data.nps.teacher_breakdown?.promoters + data.nps.org_breakdown?.promoters}</Badge>
        <Badge variant="outline">👌 Neutros: {data.nps.teacher_breakdown?.passives + data.nps.org_breakdown?.passives}</Badge>
        <Badge variant="outline">👎 Detratores: {data.nps.teacher_breakdown?.detractors + data.nps.org_breakdown?.detractors}</Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full">
          <TabsTrigger value="respostas" className="flex-1" data-testid="tab-respostas">Respostas</TabsTrigger>
          <TabsTrigger value="nps" className="flex-1">Distribuição</TabsTrigger>
          <TabsTrigger value="quality" className="flex-1">Qualidade</TabsTrigger>
          <TabsTrigger value="engagement" className="flex-1">Engajamento</TabsTrigger>
          <TabsTrigger value="testimonials" className="flex-1">Depoimentos</TabsTrigger>
        </TabsList>

        <TabsContent value="respostas" className="mt-3">
          <DynamicSummary summary={data.dynamic_summary} />
        </TabsContent>

        <TabsContent value="nps" className="mt-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2">Distribuição de notas (0–10)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={histogramData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="score" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Respostas">
                    {histogramData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-3 mt-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />0-6 detratores</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />7-8 neutros</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />9-10 promotores</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality" className="mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {distItems.map((item) => {
              const dist = data.distribution[item.key] || {};
              const total = dist.total || 1;
              return (
                <Card key={item.key}>
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-[hsl(var(--primary))]">{item.label}</p>
                    <div className="flex h-6 rounded-md overflow-hidden mt-2 border border-[hsl(var(--border))]">
                      {["otimo", "bom", "regular", "ruim", "pessimo"].map((r) => {
                        const w = ((dist[r] || 0) / total) * 100;
                        return w > 0 ? (
                          <div key={r} style={{ width: `${w}%`, backgroundColor: ratingColors[r] }} title={`${r}: ${dist[r]}`} />
                        ) : null;
                      })}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                      {["otimo", "bom", "regular", "ruim", "pessimo"].map((r) =>
                        dist[r] > 0 ? (
                          <span key={r} className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: ratingColors[r] }} />
                            {r === "otimo" ? "Ótimo" : r === "bom" ? "Bom" : r === "regular" ? "Reg." : r === "ruim" ? "Ruim" : "Pés."}: {dist[r]}
                          </span>
                        ) : null
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="engagement" className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Card><CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Primeira vez</p>
              <p className="text-lg font-bold text-[hsl(var(--primary))] mt-0.5">{data.engagement.first_time.yes}</p>
              <p className="text-[10px] text-muted-foreground">de {data.engagement.first_time.yes + data.engagement.first_time.no}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Vão retornar</p>
              <p className="text-lg font-bold text-emerald-600 mt-0.5">{data.engagement.will_return.yes}</p>
              <p className="text-[10px] text-muted-foreground">de {data.engagement.will_return.yes + data.engagement.will_return.no}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Têm Instagram</p>
              <p className="text-lg font-bold text-[hsl(var(--accent))] mt-0.5">{data.engagement.instagram.yes}</p>
              <p className="text-[10px] text-muted-foreground">de {data.engagement.instagram.yes + data.engagement.instagram.no}</p>
            </CardContent></Card>
          </div>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2">Por que escolheram o CEAP</p>
              <div className="space-y-1.5">
                {Object.entries(data.engagement.reasons || {}).filter(([k]) => k !== "total_respondentes").map(([k, v]) => {
                  const labels = {
                    email: "Recebeu e-mail", whatsapp: "Recebeu WhatsApp", telefone: "Telefonema",
                    qualidade: "Qualidade reconhecida", falta_opcao: "Falta de opção na região", outros: "Outros",
                  };
                  const total = data.engagement.reasons.total_respondentes || 1;
                  const pct = Math.round((v / total) * 100);
                  return v > 0 ? (
                    <div key={k}>
                      <div className="flex justify-between text-[11px] mb-0.5">
                        <span className="text-muted-foreground">{labels[k]}</span>
                        <span className="font-medium">{v} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                        <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="testimonials" className="mt-3">
          {data.testimonials.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhum depoimento foi deixado neste curso.</p>
          ) : (
            <div className="space-y-2">
              {data.testimonials.map((t) => (
                <Card key={t.id}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2 mb-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[hsl(var(--primary))]">{t.user_name}</p>
                        <p className="text-[10px] font-mono text-muted-foreground">{t.ceap_id}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px]">P:{t.teacher_score} O:{t.org_score}</Badge>
                    </div>
                    <p className="text-sm text-[hsl(var(--primary))] italic leading-relaxed pl-5">"{t.testimonial}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onExport} className="flex-1" data-testid="admin-export-survey-csv">
          <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
        </Button>
        <Button
          onClick={() => downloadReport("general")}
          disabled={pdfBusy !== null || data.responses_count === 0}
          className="flex-1"
          data-testid="admin-survey-report-general"
        >
          {pdfBusy === "general" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileBarChart2 className="w-4 h-4 mr-1.5" />}
          Relatório geral (PDF)
        </Button>
        <Button
          variant="outline"
          onClick={() => downloadReport("professor")}
          disabled={pdfBusy !== null || data.responses_count === 0}
          className="flex-1"
          data-testid="admin-survey-report-professor"
        >
          {pdfBusy === "professor" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <GraduationCap className="w-4 h-4 mr-1.5" />}
          Relatório do professor (PDF)
        </Button>
      </div>
    </div>
  );
}

export default function AdminPesquisas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Filtros do dashboard (combináveis)
  const [filters, setFilters] = useState({ month: "todos", year: "todos", estado: "todos", area: "todos" });
  // anos disponíveis (deduce a partir das respostas)
  const [years, setYears] = useState([]);
  // estados disponíveis (deduce a partir dos cursos)
  const [estados, setEstados] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.month !== "todos") params.month = filters.month;
      if (filters.year !== "todos") params.year = filters.year;
      if (filters.estado !== "todos") params.estado = filters.estado;
      if (filters.area !== "todos") params.area = filters.area;
      const r = await api.get("/admin/surveys/overview", { params });
      setData(r.data);
      // Deduz anos e estados disponíveis a partir do payload (se backend não retornar, fica vazio)
      if (Array.isArray(r.data?.available_years)) setYears(r.data.available_years);
      if (Array.isArray(r.data?.available_estados)) setEstados(r.data.available_estados);
    } catch (e) {
      toast.error("Erro ao carregar pesquisas");
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year, filters.estado, filters.area]);

  // Carrega ao mudar filtro
  useEffect(() => { load(); }, [load]);

  const hasActiveFilters = filters.month !== "todos" || filters.year !== "todos" || filters.estado !== "todos" || filters.area !== "todos";
  const clearFilters = () => setFilters({ month: "todos", year: "todos", estado: "todos", area: "todos" });

  const MONTH_NAMES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];

  // Descrição legível dos filtros ativos (vai no cabeçalho do PDF)
  const buildFiltersDesc = () => {
    const parts = [];
    if (filters.month !== "todos") parts.push(`Mês: ${MONTH_NAMES[parseInt(filters.month, 10) - 1] || filters.month}`);
    if (filters.year !== "todos") parts.push(`Ano: ${filters.year}`);
    if (filters.estado !== "todos") parts.push(`UF: ${filters.estado}`);
    if (filters.area !== "todos") parts.push(`Área: ${filters.area === "licitacoes" ? "Licitações" : filters.area === "legislativo" ? "Legislativo" : filters.area}`);
    return parts.join(" · ");
  };

  if (loading) return (
    <AdminLayout title="Pesquisas & NPS">
      <div className="space-y-3 max-w-5xl">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </AdminLayout>
  );

  const g = data?.global || {};
  const courses = data?.courses || [];
  const coursesWithResponses = courses.filter((c) => c.responses_count > 0);

  // Dados para gráfico de NPS por curso
  const chartData = coursesWithResponses.slice(0, 10).map((c) => ({
    name: (c.course_title || "").length > 25 ? (c.course_title || "").slice(0, 25) + "…" : c.course_title,
    NPS: c.nps_geral,
    fill: c.nps_geral >= 50 ? "#10b981" : c.nps_geral >= 0 ? "#f59e0b" : "#ef4444",
  }));

  return (
    <AdminLayout title="Pesquisas & NPS">
    <div className="space-y-5 max-w-5xl" data-testid="admin-pesquisas">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-muted-foreground">Dashboard de satisfação dos alunos por curso (escala 0–10).</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-pesquisas-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Atualizar
        </Button>
      </div>

      {/* ===== Filtros combináveis ===== */}
      <Card className="rounded-xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-[hsl(var(--primary))]">Filtros do dashboard</p>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters" className="h-7">
                <FilterX className="w-3.5 h-3.5 mr-1" /> Limpar
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Mês</label>
              <Select value={filters.month} onValueChange={(v) => setFilters(f => ({ ...f, month: v }))}>
                <SelectTrigger data-testid="filter-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os meses</SelectItem>
                  {MONTH_NAMES.map((m, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Ano</label>
              <Select value={filters.year} onValueChange={(v) => setFilters(f => ({ ...f, year: v }))}>
                <SelectTrigger data-testid="filter-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os anos</SelectItem>
                  {(years.length ? years : [new Date().getFullYear()]).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Estado (UF)</label>
              <Select value={filters.estado} onValueChange={(v) => setFilters(f => ({ ...f, estado: v }))}>
                <SelectTrigger data-testid="filter-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os estados</SelectItem>
                  {(estados.length ? estados : ["SC", "MG", "PR", "DF", "BA"]).map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Núcleo</label>
              <Select value={filters.area} onValueChange={(v) => setFilters(f => ({ ...f, area: v }))}>
                <SelectTrigger data-testid="filter-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os núcleos</SelectItem>
                  <SelectItem value="legislativo">Legislativo</SelectItem>
                  <SelectItem value="licitacoes">Licitações</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="mt-3 text-[11px] text-muted-foreground">
              Mostrando: {g.responses_total || 0} respostas que correspondem aos filtros.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cards globais */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <NpsCard label="NPS Geral" value={g.nps_geral || 0} classification={g.classification || "razoavel"} icon={Sparkles} sub={`${g.responses_total || 0} respostas no total`} />
        <NpsCard label="NPS Professores" value={g.nps_teachers || 0} classification={g.classification || "razoavel"} icon={GraduationCap} />
        <NpsCard label="NPS Organização" value={g.nps_organization || 0} classification={g.classification || "razoavel"} icon={Users} />
      </div>

      {/* Gráfico NPS por curso */}
      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-[hsl(var(--primary))] mb-2">NPS por curso (top 10)</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ left: 0, right: 10, top: 5, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={70} fontSize={11} />
                <YAxis domain={[-100, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="NPS" name="NPS Geral">
                  {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex gap-3 mt-2 text-[11px] text-muted-foreground justify-center">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Excelente (≥50)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />Razoável (0–49)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />Crítico (&lt;0)</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de cursos */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b">
            <p className="text-sm font-semibold text-[hsl(var(--primary))]">Cursos com pesquisas</p>
            <p className="text-xs text-muted-foreground">Clique em um curso para ver detalhes, depoimentos e exportar respostas.</p>
          </div>
          {courses.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nenhum curso cadastrado.</p>
          ) : (
            <div className="divide-y">
              {courses.map((c) => (
                <button
                  key={c.course_id}
                  type="button"
                  onClick={() => c.responses_count > 0 && setSelectedCourse(c.course_id)}
                  disabled={c.responses_count === 0}
                  className={`w-full text-left p-4 transition-colors ${c.responses_count > 0 ? "hover:bg-[hsl(var(--muted))]/30 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                  data-testid={`admin-survey-course-${c.course_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-semibold text-sm text-[hsl(var(--primary))] truncate">{c.course_title}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1.5 items-center">
                        <Badge variant="outline" className="text-[10px]">{c.responses_count} respostas</Badge>
                        <Badge variant="outline" className="text-[10px]">Taxa: {c.response_rate}%</Badge>
                        {c.responses_count > 0 && (
                          <>
                            <Badge className="text-[10px] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/30">
                              Prof: {c.nps_teachers >= 0 ? "+" : ""}{c.nps_teachers}
                            </Badge>
                            <Badge className="text-[10px] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30">
                              Org: {c.nps_organization >= 0 ? "+" : ""}{c.nps_organization}
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                    {c.responses_count > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground">NPS Geral</p>
                          <p className={`font-display text-lg font-bold ${c.nps_geral >= 50 ? "text-emerald-600" : c.nps_geral >= 0 ? "text-amber-600" : "text-rose-600"}`}>
                            {c.nps_geral >= 0 ? "+" : ""}{c.nps_geral}
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">Sem respostas</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalhe */}
      <Dialog open={!!selectedCourse} onOpenChange={(o) => !o && setSelectedCourse(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {courses.find((c) => c.course_id === selectedCourse)?.course_title}
            </DialogTitle>
            <DialogDescription>Análise completa de pesquisas e NPS deste curso</DialogDescription>
          </DialogHeader>
          {selectedCourse && <CourseDetail courseId={selectedCourse} onClose={() => setSelectedCourse(null)} filtersDesc={buildFiltersDesc()} />}
        </DialogContent>
      </Dialog>
    </div>
    </AdminLayout>
  );
}
